import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthProvider as RefineAuthProvider } from '@refinedev/core';
import { AdminUser, AdminRole } from '@/types';
import { useAuthStore, cleanupAuthStore, UserProfile, hasPermission as storeHasPermission, hasRole as storeHasRole } from '@/stores/authStore';

/**
 * Admin Authentication Provider
 *
 * Uses Supabase Auth via Zustand store for authentication.
 * Provides backward-compatible interface for existing components.
 */

/**
 * Map UserProfile from Supabase to AdminUser for backward compatibility
 */
const mapProfileToAdminUser = (profile: UserProfile | null): AdminUser | null => {
  if (!profile) return null;

  // Split full_name into firstName/lastName
  const nameParts = (profile.full_name || '').split(' ');
  const firstName = nameParts[0] || 'Admin';
  const lastName = nameParts.slice(1).join(' ') || 'User';

  // Get role from preferences or default to super_admin for admins
  const preferences = profile.preferences as Record<string, unknown> | undefined;
  const role = (preferences?.role as AdminRole) || 'super_admin';

  // Get permissions from preferences or default based on role
  const permissions = (preferences?.permissions as string[]) || getPermissionsForRole(role);

  return {
    id: profile.id,
    username: profile.email,
    email: profile.email,
    firstName,
    lastName,
    role,
    permissions,
    type: 'admin',
    isActive: profile.status === 'active',
    createdAt: profile.created_at,
    updatedAt: profile.updated_at,
  };
};

/**
 * Get default permissions for a role
 */
const getPermissionsForRole = (role: AdminRole): string[] => {
  switch (role) {
    case 'super_admin':
      return ['all', 'manage_partners', 'manage_users', 'view_analytics', 'manage_system', 'manage_tickets', 'manage_staff'];
    case 'partner_manager':
      return ['manage_partners', 'view_analytics', 'manage_staff'];
    case 'customer_support':
      return ['manage_tickets', 'view_customers'];
    case 'analyst':
      return ['view_analytics', 'export_data'];
    default:
      return [];
  }
};

interface AuthContextType {
  user: AdminUser | null;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: AdminRole) => boolean;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    profile,
    isLoading,
    isAuthenticated,
    login: storeLogin,
    logout: storeLogout,
    initializeAuth
  } = useAuthStore();

  const [user, setUser] = useState<AdminUser | null>(null);

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();

    // Cleanup auth subscription on unmount to prevent memory leaks
    return () => {
      cleanupAuthStore();
    };
  }, [initializeAuth]);

  // Map profile to AdminUser when it changes
  useEffect(() => {
    const adminUser = mapProfileToAdminUser(profile);
    setUser(adminUser);
  }, [profile]);

  const login = async (email: string, password: string, _mfaCode?: string) => {
    const success = await storeLogin(email, password);
    if (!success) {
      throw new Error('Login failed');
    }
  };

  const logout = async () => {
    await storeLogout();
    setUser(null);
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return user.permissions.includes(permission) || user.permissions.includes('all');
  };

  const hasRole = (role: AdminRole): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    hasPermission,
    hasRole,
    isAuthenticated,
    loading: isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Refine Auth Provider
 * Integrates with Refine's auth system using Supabase Auth
 */
export const refineAuthProvider: RefineAuthProvider = {
  login: async ({ email, password }) => {
    try {
      const success = await useAuthStore.getState().login(email, password);

      if (!success) {
        return {
          success: false,
          error: 'Login failed. Please check your credentials.',
        };
      }

      console.log('[RefineAuthProvider] Login successful, redirecting to dashboard');

      // Use replace() for clean navigation
      setTimeout(() => {
        window.location.replace('/dashboard');
      }, 100);

      return { success: true };
    } catch (error: unknown) {
      console.error('[RefineAuthProvider] Login failed:', error);
      const message = error instanceof Error ? error.message : 'Login failed';
      return {
        success: false,
        error: message,
      };
    }
  },

  logout: async () => {
    await useAuthStore.getState().logout();
    return { success: true, redirectTo: '/login' };
  },

  check: async () => {
    const { isAuthenticated, isAdmin } = useAuthStore.getState();
    return { authenticated: isAuthenticated && isAdmin };
  },

  getPermissions: async () => {
    const { profile } = useAuthStore.getState();
    if (!profile) return [];
    const preferences = profile.preferences as Record<string, unknown> | undefined;
    return (preferences?.permissions as string[]) || getPermissionsForRole('super_admin');
  },

  getIdentity: async () => {
    const { profile } = useAuthStore.getState();
    return mapProfileToAdminUser(profile);
  },

  onError: async (error) => {
    if ((error as { status?: number })?.status === 401) {
      return { logout: true };
    }
    return {};
  },
};
